import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { Link } from "react-router-dom";
import "./ArtistsShowcase.scss";

export const ArtistsShowcase = ({
  artists,
  viewAllPath = "/artists",
}) => {
  const trackRef = useRef(null);

  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const goToIndex = useCallback(
    (requestedIndex) => {
      if (!artists.length) return;

      const index =
        (requestedIndex + artists.length) %
        artists.length;

      setActiveIndex(index);

      const track = trackRef.current;

      if (!track) return;

      const card = track.children[index];

      if (!card) return;

      track.scrollTo({
        left: card.offsetLeft - track.offsetLeft,
        behavior: "smooth",
      });
    },
    [artists.length]
  );

  const next = useCallback(() => {
    goToIndex(activeIndex + 1);
  }, [activeIndex, goToIndex]);

  const previous = useCallback(() => {
    goToIndex(activeIndex - 1);
  }, [activeIndex, goToIndex]);

  useEffect(() => {
    if (paused || artists.length <= 1) return;

    const timer = setInterval(() => {
      setActiveIndex((current) => {
        const nextIndex =
          (current + 1) % artists.length;

        const track = trackRef.current;

        if (track) {
          const card = track.children[nextIndex];

          if (card) {
            track.scrollTo({
              left:
                card.offsetLeft -
                track.offsetLeft,
              behavior: "smooth",
            });
          }
        }

        return nextIndex;
      });
    }, 3500);

    return () => clearInterval(timer);
  }, [artists.length, paused]);

  return (
    <section className="artists-showcase">
      <div className="artists-showcase-inner">

        <div className="artists-topbar">
          <div>
            <span className="artists-number">
              03
            </span>

            <span className="artists-eyebrow">
              ARTISTS
            </span>
          </div>

          <Link
            to={viewAllPath}
            className="artists-view-all"
          >
            VIEW ALL <span>↗</span>
          </Link>
        </div>

        <div className="artists-heading">
          <h2>
            THE VOICES
            <span>BEHIND THE MOVEMENT.</span>
          </h2>

          <p>
            Artists who stepped into LHANGAR
            and became part of a creative
            movement built around talent,
            authenticity, collaboration and
            Moroccan rap culture.
          </p>
        </div>

        <div className="artists-slider-header">
          <div>
            <small>DISCOVER</small>
            <strong>Featured artists</strong>
          </div>

          <div className="artists-controls">
            <button
              onClick={previous}
              aria-label="Previous artist"
            >
              ←
            </button>

            <span className="artists-counter">
              {String(activeIndex + 1).padStart(2, "0")}
              {" / "}
              {String(artists.length).padStart(2, "0")}
            </span>

            <button
              onClick={next}
              aria-label="Next artist"
            >
              →
            </button>
          </div>
        </div>

        <div
          className="artists-track"
          ref={trackRef}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={() => setPaused(true)}
          onTouchEnd={() => setPaused(false)}
        >
          {artists.map((artist, index) => (
            <article
              key={`${artist.name}-${index}`}
              className={
                index === activeIndex
                  ? "artist-card-modern active"
                  : "artist-card-modern"
              }
              onClick={() => goToIndex(index)}
            >
              <div className="artist-card-image">
                <img
                  src={artist.image}
                  alt={artist.name}
                  loading="lazy"
                />

                <div className="artist-card-gradient" />

                <span className="artist-card-number">
                  {String(index + 1).padStart(
                    2,
                    "0"
                  )}
                </span>

                <div className="artist-card-content">
                  <small>
                    {artist.role ||
                      "LHANGAR ARTIST"}
                  </small>

                  <h3>{artist.name}</h3>

                  <span className="artist-card-arrow">
                    ↗
                  </span>
                </div>
              </div>
            </article>
          ))}
        </div>

      </div>
    </section>
  );
};